import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Tooltip, Chip, Avatar, Switch, FormControlLabel, TextField, Select, MenuItem, FormControl, InputLabel, InputAdornment } from '@mui/material';
import { toast } from 'react-toastify';
import moment from 'moment-jalaali';
import { formatPersianMoneyInput, parsePersianMoneyInput, amountToPersianWords, toPersianDigitsStr } from '../utils/persianNumber';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import BlockIcon from '@mui/icons-material/Block';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';

// Services and types
import storeService, { StoreStatus, StoreFilters, Store } from '../services/storeService';
import StoreEscrowDialog from '../components/stores/StoreEscrowDialog';

import './EntityManagement.css';

// تابع تبدیل تاریخ به فرمت مناسب
const formatDate = (date: string): string => {
  if (!date) return '';
  return moment(date).locale('fa').format('jYYYY/jMM/jDD HH:mm');
};

// تابع نمایش اطلاعات کاربر
const formatUserId = (userId: string): string => {
  if (!userId) return 'کاربر نامشخص';
  if (userId === '6804cf349f1fe0b886af7a94') return 'Super Admin';
  return userId.substring(0, 6) + '...';
};

const formatCommission = (commission: number | null) => {
  return commission !== null ? `${commission}%` : 'پیش‌فرض';
};

// نمایش یک وضعیت کمیسیون در تاریخچه — همه‌ی چهار حالت + پیش‌فرض
const formatCommissionHistoryValue = (
  commission: number | null | undefined,
  tiers?: Array<{ upTo: number | null; percent: number }> | null,
  fixed?: number | null,
  fixedTiers?: Array<{ upTo: number | null; amount: number }> | null
) => {
  if (fixedTiers && fixedTiers.length > 0) return `مبلغ‌ثابت پلکانی (${fixedTiers.length} پله)`;
  if (fixed !== null && fixed !== undefined) return `${fixed.toLocaleString('fa-IR')} تومان ثابت`;
  if (tiers && tiers.length > 0) return `پلکانی (${tiers.length} پله)`;
  return formatCommission(commission ?? null);
};

type CommissionTier = { upTo: number | null; percent: number };
type CommissionFixedTier = { upTo: number | null; amount: number };

// خلاصهٔ وضعیت کمیسیون فروشگاه — همان ترتیب اولویت بک‌اند:
// مبلغ‌ثابت پلکانی > مبلغ‌ثابت > پلکان درصدی > درصد ثابت > پیش‌فرض
const describeCommission = (store?: {
  customCommission?: number | null;
  customCommissionTiers?: CommissionTier[] | null;
  customCommissionFixed?: number | null;
  customCommissionFixedTiers?: CommissionFixedTier[] | null;
} | null) => {
  if (store?.customCommissionFixedTiers && store.customCommissionFixedTiers.length > 0) {
    return `مبلغ‌ثابت پلکانی (${store.customCommissionFixedTiers.length} پله)`;
  }
  if (store?.customCommissionFixed !== null && store?.customCommissionFixed !== undefined) {
    return `${formatPersianMoneyInput(String(store.customCommissionFixed))} تومان ثابت`;
  }
  if (store?.customCommissionTiers && store.customCommissionTiers.length > 0) {
    return `پلکانی (${store.customCommissionTiers.length} پله)`;
  }
  if (store?.customCommission !== null && store?.customCommission !== undefined) {
    return `${store.customCommission}%`;
  }
  return 'پیش‌فرض';
};

// محاسبهٔ کارمزد «مبلغ‌ثابت پلکانی» (bracket) — آینهٔ computeFixedTierFee بک‌اند (فقط پیش‌نمایش)
// کل مبلغ در پلهٔ خودش می‌افتد و مبلغ ثابت همان پله را می‌دهد (سقف: خود مبلغ)
const computeFixedTierFeePreview = (
  amount: number,
  tiers: CommissionFixedTier[]
): { fee: number; bracket: { index: number; from: number; to: number | null; amount: number } | null } => {
  if (amount <= 0 || tiers.length === 0) return { fee: 0, bracket: null };
  let prevCap = 0;
  for (let i = 0; i < tiers.length; i++) {
    const tier = tiers[i];
    if (tier.upTo === null || amount <= tier.upTo) {
      return {
        fee: Math.min(Math.max(0, Math.round(tier.amount)), amount),
        bracket: { index: i + 1, from: prevCap, to: tier.upTo, amount: tier.amount }
      };
    }
    prevCap = tier.upTo;
  }
  const last = tiers[tiers.length - 1];
  return {
    fee: Math.min(Math.max(0, Math.round(last.amount)), amount),
    bracket: { index: tiers.length, from: prevCap, to: last.upTo, amount: last.amount }
  };
};

// محاسبهٔ کارمزد پلکانی حاشیه‌ای — آینهٔ commissionFee.service بک‌اند (فقط پیش‌نمایش)
const computeMarginalFeePreview = (
  amount: number,
  tiers: CommissionTier[]
): { fee: number; rows: Array<{ index: number; from: number; to: number | null; percent: number; portion: number; fee: number }> } => {
  let fee = 0;
  let prevCap = 0;
  const rows: Array<{ index: number; from: number; to: number | null; percent: number; portion: number; fee: number }> = [];
  for (let i = 0; i < tiers.length; i++) {
    const tier = tiers[i];
    if (amount <= prevCap) break;
    const cap = tier.upTo === null ? Infinity : tier.upTo;
    const portion = Math.min(amount, cap) - prevCap;
    if (portion > 0) {
      const rowFee = Math.round(portion * (tier.percent / 100));
      rows.push({ index: i + 1, from: prevCap, to: tier.upTo, percent: tier.percent, portion, fee: rowFee });
      fee += rowFee;
    }
    if (tier.upTo === null) break;
    prevCap = tier.upTo;
  }
  return { fee, rows };
};

const formatStatus = (status: string) => {
  switch(status) {
    case 'ACTIVE': return 'فعال';
    case 'INACTIVE': return 'غیرفعال';
    case 'SUSPENDED': return 'معلق';
    default: return status;
  }
};

const StoreManagement: React.FC = () => {
  // State
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [networkError, setNetworkError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterStatus, setFilterStatus] = useState<StoreStatus | ''>('');
  const [filterVerified, setFilterVerified] = useState<string>('');
  const [filterKind, setFilterKind] = useState<'' | 'business' | 'personal'>('');

  // Dialogs
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [commissionDialogOpen, setCommissionDialogOpen] = useState(false);
  // 🔓 پنجرهٔ آزادسازی وجه همین فروشگاه (مدت گارانتی اختصاصی + آزادسازی/تعویق دستی)
  const [escrowStore, setEscrowStore] = useState<Store | null>(null);
  const [currentStore, setCurrentStore] = useState<Store | null>(null);

  // Form inputs
  const [statusReason, setStatusReason] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<StoreStatus>(StoreStatus.ACTIVE);
  const [verificationNote, setVerificationNote] = useState('');
  const [customCommission, setCustomCommission] = useState<string>('');
  const [commissionMode, setCommissionMode] = useState<'default' | 'flat' | 'tiers' | 'fixed' | 'fixed-tiers'>('default');
  const [tierRows, setTierRows] = useState<Array<{ upTo: string; percent: string }>>([{ upTo: '', percent: '' }]);
  const [customCommissionFixed, setCustomCommissionFixed] = useState<string>('');
  const [fixedTierRows, setFixedTierRows] = useState<Array<{ upTo: string; amount: string }>>([{ upTo: '', amount: '' }]);
  const [tierPreviewAmount, setTierPreviewAmount] = useState<string>('50000000');
  const [commissionNotes, setCommissionNotes] = useState('');

  // Fetch stores
  const fetchStores = useCallback(async () => {
    setLoading(true);
    setNetworkError(null);
    try {
      const storeFilters: StoreFilters = {};
      if (searchTerm) storeFilters.name = searchTerm;
      if (filterStatus) storeFilters.status = filterStatus as StoreStatus;
      if (filterVerified === 'verified') storeFilters.verificationStatus = true;
      if (filterVerified === 'unverified') storeFilters.verificationStatus = false;
      if (filterKind) storeFilters.kind = filterKind;

      const response = await storeService.getStores(page, rowsPerPage, storeFilters);

      if (response.items && response.items.length > 0) {
        const transformedStores = response.items.map((adminStore: any) => ({
          _id: adminStore._id || adminStore.id,
          kind: adminStore.kind || 'business',
          name: adminStore.name,
          slug: adminStore.slug,
          description: adminStore.description,
          logo: adminStore.logo,
          banner: adminStore.banner || '',
          email: adminStore.email,
          phone: adminStore.phone,
          address: adminStore.address,
          city: adminStore.city,
          province: adminStore.province,
          postalCode: adminStore.postalCode,
          socialLinks: adminStore.socialLinks,
          categories: adminStore.categories || [],
          status: adminStore.status,
          isVerified: adminStore.isVerified,
          verificationNotes: adminStore.verificationNotes || '',
          customCommission: adminStore.customCommission,
          // ✅ FIX (2026-08-16): این سه فیلد قبلاً map نمی‌شدند و دیالوگ کمیسیون
          // پیکربندی پلکانی موجود را نمی‌دید
          customCommissionTiers: adminStore.customCommissionTiers,
          customCommissionFixed: adminStore.customCommissionFixed,
          customCommissionFixedTiers: adminStore.customCommissionFixedTiers,
          commissionNotes: adminStore.commissionNotes || '',
          policies: {},
          rating: adminStore.rating,
          userId: adminStore.userId || adminStore.owner?.id || '',
          createdAt: adminStore.createdAt,
          updatedAt: adminStore.updatedAt,
          statusChangeHistory: adminStore.statusChangeHistory,
          commissionChangeHistory: adminStore.commissionChangeHistory,
          verificationChangeHistory: adminStore.verificationChangeHistory
        }));
        setStores(transformedStores);
        setTotalItems(response.meta?.totalItems || 0);
      } else {
        setStores([]);
        setTotalItems(0);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? `خطا در ارتباط با سرور: ${error.message}` : 'خطا در دریافت لیست فروشگاه‌ها';
      setNetworkError(errorMessage);
      setStores([]);
      setTotalItems(0);
      toast.error('خطا در دریافت لیست فروشگاه‌ها');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, filterStatus, filterVerified, filterKind]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  // Handlers
  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleOpenDetailsDialog = (store: Store) => {
    setCurrentStore(store);
    setDetailsDialogOpen(true);
  };

  const handleOpenStatusDialog = (store: Store) => {
    setCurrentStore(store);
    setSelectedStatus(store.status as StoreStatus);
    setStatusReason('');
    setStatusDialogOpen(true);
  };

  const handleOpenVerifyDialog = (store: Store) => {
    setCurrentStore(store);
    setVerificationNote('');
    setVerifyDialogOpen(true);
  };

  const handleOpenCommissionDialog = (store: Store) => {
    setCurrentStore(store);
    const hasFixedTiers = !!(store.customCommissionFixedTiers && store.customCommissionFixedTiers.length > 0);
    const hasFixed = store.customCommissionFixed !== null && store.customCommissionFixed !== undefined;
    const hasTiers = !!(store.customCommissionTiers && store.customCommissionTiers.length > 0);
    const hasFlat = store.customCommission !== null && store.customCommission !== undefined;
    // همان ترتیب اولویت بک‌اند
    setCommissionMode(
      hasFixedTiers ? 'fixed-tiers' : hasFixed ? 'fixed' : hasTiers ? 'tiers' : hasFlat ? 'flat' : 'default'
    );
    setCustomCommission(hasFlat ? String(store.customCommission) : '');
    setCustomCommissionFixed(hasFixed ? String(store.customCommissionFixed) : '');
    setTierRows(
      hasTiers
        ? store.customCommissionTiers!.map((t) => ({
            upTo: t.upTo === null ? '' : String(t.upTo),
            percent: String(t.percent)
          }))
        : [
            { upTo: '20000000', percent: '5' },
            { upTo: '', percent: '2' }
          ]
    );
    setFixedTierRows(
      hasFixedTiers
        ? store.customCommissionFixedTiers!.map((t) => ({
            upTo: t.upTo === null ? '' : String(t.upTo),
            amount: String(t.amount)
          }))
        : [
            { upTo: '20000000', amount: '200000' },
            { upTo: '', amount: '500000' }
          ]
    );
    setCommissionNotes('');
    setCommissionDialogOpen(true);
  };

  const handleCloseDialogs = () => {
    setDetailsDialogOpen(false);
    setStatusDialogOpen(false);
    setVerifyDialogOpen(false);
    setCommissionDialogOpen(false);
    setCurrentStore(null);
  };

  const handleUpdateStatus = async () => {
    if (!currentStore) return;
    try {
      await storeService.updateStoreStatus(currentStore._id, selectedStatus, statusReason);
      handleCloseDialogs();
      fetchStores();
      toast.success('وضعیت فروشگاه بروزرسانی شد');
    } catch (error) {
      console.error('Error updating store status:', error);
      toast.error('خطا در بروزرسانی وضعیت');
    }
  };

  const handleUpdateVerification = async (verified: boolean) => {
    if (!currentStore) return;
    try {
      await storeService.updateVerificationStatus(currentStore._id, verified, verificationNote);
      handleCloseDialogs();
      fetchStores();
      toast.success('وضعیت تأیید فروشگاه بروزرسانی شد');
    } catch (error) {
      console.error('Error updating verification status:', error);
      toast.error('خطا در بروزرسانی وضعیت تأیید');
    }
  };

  // پارس و اعتبارسنجی پله‌های فرم — خروجی null یعنی خطا (toast داده شده)
  const parseTierRows = (): CommissionTier[] | null => {
    const parsed: CommissionTier[] = [];
    let prevCap = 0;
    for (let i = 0; i < tierRows.length; i++) {
      const row = tierRows[i];
      const percent = Number(row.percent);
      if (row.percent.trim() === '' || isNaN(percent) || percent < 0 || percent > 100) {
        toast.error(`درصد پله ${i + 1} باید عددی بین 0 تا 100 باشد`);
        return null;
      }
      const isLast = i === tierRows.length - 1;
      const upTo = row.upTo.trim() === '' ? null : Number(row.upTo);
      if (upTo === null) {
        if (!isLast) {
          toast.error('فقط آخرین پله می‌تواند بدون سقف (نامحدود) باشد');
          return null;
        }
      } else if (isNaN(upTo) || upTo <= prevCap) {
        toast.error(`سقف پله ${i + 1} باید عددی بزرگ‌تر از سقف پله قبل باشد`);
        return null;
      } else {
        prevCap = upTo;
      }
      parsed.push({ upTo, percent });
    }
    if (parsed.length === 0) {
      toast.error('حداقل یک پله تعریف کنید');
      return null;
    }
    // پوشش تا بی‌نهایت: اگر آخرین پله سقف داشت، همان نرخ ادامه می‌یابد
    if (parsed[parsed.length - 1].upTo !== null) {
      parsed.push({ upTo: null, percent: parsed[parsed.length - 1].percent });
    }
    return parsed;
  };

  // پارس و اعتبارسنجی پله‌های مبلغ‌ثابت — خروجی null یعنی خطا (toast داده شده)
  const parseFixedTierRows = (): CommissionFixedTier[] | null => {
    const parsed: CommissionFixedTier[] = [];
    let prevCap = 0;
    for (let i = 0; i < fixedTierRows.length; i++) {
      const row = fixedTierRows[i];
      const amount = Number(row.amount);
      if (row.amount.trim() === '' || isNaN(amount) || amount < 0) {
        toast.error(`مبلغ کارمزد پله ${i + 1} باید عددی صفر یا بزرگ‌تر (به تومان) باشد`);
        return null;
      }
      const isLast = i === fixedTierRows.length - 1;
      const upTo = row.upTo.trim() === '' ? null : Number(row.upTo);
      if (upTo === null) {
        if (!isLast) {
          toast.error('فقط آخرین پله می‌تواند بدون سقف (نامحدود) باشد');
          return null;
        }
      } else if (isNaN(upTo) || upTo <= prevCap) {
        toast.error(`سقف پله ${i + 1} باید عددی بزرگ‌تر از سقف پله قبل باشد`);
        return null;
      } else {
        prevCap = upTo;
      }
      parsed.push({ upTo, amount: Math.round(amount) });
    }
    if (parsed.length === 0) {
      toast.error('حداقل یک پله تعریف کنید');
      return null;
    }
    // پوشش تا بی‌نهایت: اگر آخرین پله سقف داشت، همان مبلغ ادامه می‌یابد
    if (parsed[parsed.length - 1].upTo !== null) {
      parsed.push({ upTo: null, amount: parsed[parsed.length - 1].amount });
    }
    return parsed;
  };

  const handleUpdateCommission = async () => {
    if (!currentStore) return;

    if (!commissionNotes.trim()) {
      toast.error('وارد کردن یادداشت کمیسیون الزامی است');
      return;
    }

    let commissionValue: number | null = null;
    let tiersValue: CommissionTier[] | null = null;
    let fixedValue: number | null = null;
    let fixedTiersValue: CommissionFixedTier[] | null = null;

    if (commissionMode === 'flat') {
      if (!customCommission.trim()) {
        toast.error('لطفاً مقدار کمیسیون را وارد کنید');
        return;
      }
      if (isNaN(Number(customCommission)) || Number(customCommission) < 0 || Number(customCommission) > 100) {
        toast.error('مقدار کمیسیون باید بین 0 تا 100 درصد باشد');
        return;
      }
      commissionValue = Number(customCommission);
    } else if (commissionMode === 'tiers') {
      const parsed = parseTierRows();
      if (!parsed) return;
      tiersValue = parsed;
    } else if (commissionMode === 'fixed') {
      if (!customCommissionFixed.trim()) {
        toast.error('لطفاً مبلغ ثابت کارمزد را وارد کنید');
        return;
      }
      if (isNaN(Number(customCommissionFixed)) || Number(customCommissionFixed) < 0) {
        toast.error('مبلغ ثابت کارمزد باید عددی صفر یا بزرگ‌تر (به تومان) باشد');
        return;
      }
      fixedValue = Math.round(Number(customCommissionFixed));
    } else if (commissionMode === 'fixed-tiers') {
      const parsed = parseFixedTierRows();
      if (!parsed) return;
      fixedTiersValue = parsed;
    }

    try {
      setLoading(true);
      // هر چهار فیلد صریح ارسال می‌شود — بک‌اند exclusivity را تضمین می‌کند
      // (حالت انتخابی ست و سه حالت دیگر پاک می‌شوند؛ default = همه null)
      await storeService.updateStoreCommission(
        currentStore._id,
        commissionValue,
        commissionNotes,
        tiersValue,
        fixedValue,
        fixedTiersValue
      );
      toast.success('کمیسیون فروشگاه بروزرسانی شد');
      handleCloseDialogs();
      setTimeout(() => fetchStores(), 500);
    } catch (error) {
      console.error('Error updating store commission:', error);
      toast.error('خطا در بروزرسانی کمیسیون');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSearchInput('');
    setFilterStatus('');
    setFilterVerified('');
    setFilterKind('');
    setPage(1);
  };

  const renderStatusChip = (status: StoreStatus) => {
    switch (status) {
      case StoreStatus.ACTIVE:
        return <span className="tag tag-sm tag-active">فعال</span>;
      case StoreStatus.INACTIVE:
        return <span className="tag tag-sm tag-inactive">غیرفعال</span>;
      case StoreStatus.SUSPENDED:
        return <span className="tag tag-sm" style={{ background: 'var(--color-warning-bg)', color: 'var(--color-warning-dark)' }}>معلق</span>;
      default:
        return <span className="tag tag-sm">نامشخص</span>;
    }
  };

  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const startItem = (page - 1) * rowsPerPage + 1;
  const endItem = Math.min(page * rowsPerPage, totalItems);
  const hasActiveFilters = searchTerm || filterStatus || filterVerified || filterKind;

  return (
    <div className="entity-page">
      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">مدیریت فروشگاه‌ها</h1>
          <p className="entity-subtitle">مدیریت فروشگاه‌ها، تأیید و کمیسیون</p>
        </div>
        <div className="header-actions">
          <button className="header-btn refresh" onClick={fetchStores} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><StorefrontIcon /></div>
          <div>
            <p className="entity-stat-label">کل فروشگاه‌ها</p>
            <p className="entity-stat-value">{totalItems}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon green"><CheckCircleIcon /></div>
          <div>
            <p className="entity-stat-label">فعال</p>
            <p className="entity-stat-value">{stores.filter(s => s.status === StoreStatus.ACTIVE).length}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon orange"><BlockIcon /></div>
          <div>
            <p className="entity-stat-label">معلق / غیرفعال</p>
            <p className="entity-stat-value">{stores.filter(s => s.status !== StoreStatus.ACTIVE).length}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="جستجوی فروشگاه..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">وضعیت</label>
            <select
              className="filter-field"
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value as StoreStatus | ''); setPage(1); }}
            >
              <option value="">همه وضعیت‌ها</option>
              <option value={StoreStatus.ACTIVE}>فعال</option>
              <option value={StoreStatus.INACTIVE}>غیرفعال</option>
              <option value={StoreStatus.SUSPENDED}>معلق</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">تأیید</label>
            <select
              className="filter-field"
              value={filterVerified}
              onChange={(e) => { setFilterVerified(e.target.value); setPage(1); }}
            >
              <option value="">همه تأیید</option>
              <option value="verified">تأیید شده</option>
              <option value="unverified">تأیید نشده</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">نوع</label>
            <select
              className="filter-field"
              value={filterKind}
              onChange={(e) => { setFilterKind(e.target.value as '' | 'business' | 'personal'); setPage(1); }}
            >
              <option value="">همه انواع</option>
              <option value="business">فروشگاه</option>
              <option value="personal">فروشندهٔ شخصی</option>
            </select>
          </div>

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {hasActiveFilters && (
              <button className="filter-btn outline" onClick={clearFilters}>
                پاک
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="filters-active">
            <span className="filters-label">فیلترهای فعال:</span>
            {searchTerm && (
              <span className="filter-tag">
                جستجو: {searchTerm}
                <button onClick={() => { setSearchTerm(''); setSearchInput(''); }}>×</button>
              </span>
            )}
            {filterStatus && (
              <span className="filter-tag">
                وضعیت: {formatStatus(filterStatus)}
                <button onClick={() => setFilterStatus('')}>×</button>
              </span>
            )}
            {filterVerified && (
              <span className="filter-tag">
                تأیید: {filterVerified === 'verified' ? 'تأیید شده' : 'تأیید نشده'}
                <button onClick={() => setFilterVerified('')}>×</button>
              </span>
            )}
            {filterKind && (
              <span className="filter-tag">
                نوع: {filterKind === 'personal' ? 'فروشندهٔ شخصی' : 'فروشگاه'}
                <button onClick={() => setFilterKind('')}>×</button>
              </span>
            )}
            <button className="filters-clear" onClick={clearFilters}>پاک کردن همه</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="entity-table-wrapper">
        {loading ? (
          <div className="entity-loading">
            <CircularProgress size={32} />
          </div>
        ) : networkError ? (
          <div className="entity-empty">
            <div className="entity-empty-icon" style={{ color: 'var(--color-error)' }}>⚠️</div>
            <p className="entity-empty-text">{networkError}</p>
            <button className="header-btn primary" onClick={fetchStores} style={{ marginTop: 16 }}>
              تلاش مجدد
            </button>
          </div>
        ) : stores.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><StorefrontIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">هیچ فروشگاهی یافت نشد</p>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>فروشگاه</th>
                  <th>نوع</th>
                  <th>ایمیل</th>
                  <th>وضعیت</th>
                  <th>تأیید</th>
                  <th>کمیسیون</th>
                  <th>امتیاز</th>
                  <th style={{ width: 160 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {stores.map((store) => (
                  <tr key={store._id}>
                    <td>
                      <div className="entity-info-cell">
                        <div className="entity-image">
                          {store.logo ? (
                            <img src={store.logo} alt={store.name} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                          ) : (
                            <StorefrontIcon />
                          )}
                        </div>
                        <div className="entity-info">
                          <span className="entity-name">{store.name || 'بدون نام'}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      {store.kind === 'personal' ? (
                        <span className="tag tag-sm" style={{ background: 'var(--color-info-bg, #e8f1ff)', color: 'var(--color-info-dark, #0b57d0)' }}>فروشندهٔ شخصی</span>
                      ) : (
                        <span className="tag tag-sm tag-inactive">فروشگاه</span>
                      )}
                    </td>
                    <td>{store.kind === 'personal' && /@personal\.gamizo\.ir$/i.test(store.email || '') ? <span style={{ color: 'var(--color-text-secondary)', fontSize: 12 }}>— (بدون ایمیل)</span> : (store.email || '-')}</td>
                    <td>{store.status ? renderStatusChip(store.status as StoreStatus) : '-'}</td>
                    <td>
                      {store.isVerified !== undefined ? (
                        store.isVerified ? (
                          <span className="tag tag-sm tag-active">تأیید شده</span>
                        ) : (
                          <span className="tag tag-sm" style={{ background: 'var(--color-warning-bg)', color: 'var(--color-warning-dark)' }}>تأیید نشده</span>
                        )
                      ) : '-'}
                    </td>
                    <td>
                      {(store.customCommissionFixedTiers && store.customCommissionFixedTiers.length > 0) ||
                      (store.customCommissionFixed !== undefined && store.customCommissionFixed !== null) ||
                      (store.customCommissionTiers && store.customCommissionTiers.length > 0) ||
                      (store.customCommission !== undefined && store.customCommission !== null) ? (
                        <span className="tag tag-sm tag-platform">{describeCommission(store)}</span>
                      ) : (
                        <span style={{ color: 'var(--color-text-secondary)', fontSize: 12 }}>پیش‌فرض</span>
                      )}
                    </td>
                    <td>
                      {store.rating ? (
                        <span style={{ fontSize: 13 }}>
                          {store.rating.average !== undefined ? store.rating.average.toFixed(1) : '0.0'}
                          <span style={{ color: 'var(--color-text-secondary)', marginRight: 4 }}>({store.rating.count || 0})</span>
                        </span>
                      ) : '-'}
                    </td>
                    <td>
                      <div className="actions-cell">
                        <Tooltip title="مشاهده جزئیات" arrow>
                          <button className="action-btn edit" onClick={() => handleOpenDetailsDialog(store)}>
                            <VisibilityIcon />
                          </button>
                        </Tooltip>
                        <Tooltip title="تغییر وضعیت" arrow>
                          <button className="action-btn" onClick={() => handleOpenStatusDialog(store)}>
                            <BlockIcon />
                          </button>
                        </Tooltip>
                        <Tooltip title={store.isVerified ? "لغو تأیید" : "تأیید"} arrow>
                          <button
                            className={`action-btn toggle ${store.isVerified ? 'active' : ''}`}
                            onClick={() => handleOpenVerifyDialog(store)}
                          >
                            {store.isVerified ? <CheckCircleIcon /> : <CancelIcon />}
                          </button>
                        </Tooltip>
                        <Tooltip title="کمیسیون" arrow>
                          <button className="action-btn" onClick={() => handleOpenCommissionDialog(store)}>
                            <MonetizationOnIcon />
                          </button>
                        </Tooltip>
                        <Tooltip title="آزادسازی وجه و مدت گارانتی" arrow>
                          <button className="action-btn" onClick={() => setEscrowStore(store)}>
                            <LockOpenRoundedIcon />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="entity-cards">
              {stores.map((store) => (
                <div key={store._id} className={`entity-card ${store.status === StoreStatus.SUSPENDED ? 'inactive' : ''}`}>
                  <div className="entity-card-header">
                    <div className="entity-card-image">
                      {store.logo ? (
                        <img src={store.logo} alt={store.name} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      ) : <StorefrontIcon />}
                    </div>
                    <div className="entity-card-info">
                      <div className="entity-card-name">{store.name || 'بدون نام'}</div>
                      <div className="entity-card-desc">
                        {store.email || '-'}
                        {store.rating && ` • ${store.rating.average?.toFixed(1) || '0.0'} (${store.rating.count || 0})`}
                      </div>
                    </div>
                    <div className="entity-card-status">
                      {store.status ? renderStatusChip(store.status as StoreStatus) : null}
                    </div>
                  </div>
                  <div style={{ padding: '8px 14px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <span className={`tag tag-sm ${store.isVerified ? 'tag-active' : ''}`} style={!store.isVerified ? { background: 'var(--color-warning-bg)', color: 'var(--color-warning-dark)' } : {}}>
                      {store.isVerified ? 'تأیید شده' : 'تأیید نشده'}
                    </span>
                    <span className="tag tag-sm tag-platform">
                      کمیسیون: {describeCommission(store)}
                    </span>
                  </div>
                  <div className="entity-card-actions">
                    <button className="entity-card-btn" onClick={() => handleOpenDetailsDialog(store)}>
                      <VisibilityIcon fontSize="small" /> جزئیات
                    </button>
                    <button className="entity-card-btn" onClick={() => handleOpenStatusDialog(store)}>
                      <BlockIcon fontSize="small" /> وضعیت
                    </button>
                    <button className="entity-card-btn primary" onClick={() => handleOpenVerifyDialog(store)}>
                      {store.isVerified ? <CancelIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                      {store.isVerified ? 'لغو تأیید' : 'تأیید'}
                    </button>
                    <button className="entity-card-btn" onClick={() => handleOpenCommissionDialog(store)}>
                      <MonetizationOnIcon fontSize="small" /> کمیسیون
                    </button>
                    <button className="entity-card-btn" onClick={() => setEscrowStore(store)}>
                      <LockOpenRoundedIcon fontSize="small" /> آزادسازی وجه
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="entity-pagination">
              <div className="pagination-info">
                نمایش {startItem} تا {endItem} از {totalItems}
              </div>
              <div className="pagination-controls">
                <select
                  className="pagination-select"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <button className="pagination-btn" onClick={() => setPage(page + 1)} disabled={page >= totalPages}>
                  <ChevronRightIcon fontSize="small" />
                </button>
                <span style={{ fontSize: 12 }}>{page} / {totalPages || 1}</span>
                <button className="pagination-btn" onClick={() => setPage(page - 1)} disabled={page <= 1}>
                  <ChevronLeftIcon fontSize="small" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth className="store-details-dialog">
        <div className="edit-dialog-title">
          <span>جزئیات فروشگاه</span>
          <button className="details-close" onClick={() => setDetailsDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {currentStore && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Banner */}
              {currentStore.banner && (
                <div style={{ width: '100%', height: 120, borderRadius: 8, overflow: 'hidden', background: 'var(--color-bg-default)' }}>
                  <img src={currentStore.banner} alt="banner" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                </div>
              )}

              {/* Store Header */}
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', padding: 16, background: 'var(--color-bg-default)', borderRadius: 8 }}>
                <div style={{ width: 80, height: 80, borderRadius: 8, overflow: 'hidden', background: 'var(--color-bg-paper)', border: '1px solid var(--color-border)' }}>
                  {currentStore.logo ? (
                    <img src={currentStore.logo} alt={currentStore.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-disabled)' }}>
                      <StorefrontIcon style={{ fontSize: 40 }} />
                    </div>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, marginBottom: 8 }}>{currentStore.name}</h3>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                    {currentStore.kind === 'personal' && (
                      <span className="tag tag-sm" style={{ background: 'var(--color-info-bg, #e8f1ff)', color: 'var(--color-info-dark, #0b57d0)' }}>
                        فروشندهٔ شخصی — نام/لوگو از پروفایل کاربر
                      </span>
                    )}
                    {renderStatusChip(currentStore.status as StoreStatus)}
                    <span className={`tag tag-sm ${currentStore.isVerified ? 'tag-active' : ''}`} style={!currentStore.isVerified ? { background: 'var(--color-warning-bg)', color: 'var(--color-warning-dark)' } : {}}>
                      {currentStore.isVerified ? 'تأیید شده' : 'تأیید نشده'}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                    ایجاد: {formatDate(currentStore.createdAt)} • بروزرسانی: {formatDate(currentStore.updatedAt)}
                  </div>
                </div>
              </div>

              {/* Basic Info */}
              <div className="form-grid">
                <div className="form-field">
                  <label>ایمیل</label>
                  <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13 }}>{currentStore.email || '-'}</div>
                </div>
                <div className="form-field">
                  <label>تلفن</label>
                  <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13 }}>{currentStore.phone || '-'}</div>
                </div>
                <div className="form-field">
                  <label>کمیسیون</label>
                  <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13 }}>
                    {describeCommission(currentStore)}
                    {currentStore.customCommissionTiers && currentStore.customCommissionTiers.length > 0 && (
                      <div style={{ marginTop: 6, fontSize: 12, color: 'var(--color-text-secondary)' }}>
                        {currentStore.customCommissionTiers.map((t, i) => (
                          <div key={i}>
                            {t.upTo === null
                              ? `مازاد: ${t.percent}%`
                              : `تا ${t.upTo.toLocaleString('fa-IR')} تومان: ${t.percent}%`}
                          </div>
                        ))}
                      </div>
                    )}
                    {currentStore.customCommissionFixedTiers && currentStore.customCommissionFixedTiers.length > 0 && (
                      <div style={{ marginTop: 6, fontSize: 12, color: 'var(--color-text-secondary)' }}>
                        {currentStore.customCommissionFixedTiers.map((t, i) => (
                          <div key={i}>
                            {t.upTo === null
                              ? `مازاد: ${t.amount.toLocaleString('fa-IR')} تومان ثابت`
                              : `تا ${t.upTo.toLocaleString('fa-IR')} تومان: ${t.amount.toLocaleString('fa-IR')} تومان ثابت`}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-field">
                  <label>امتیاز</label>
                  <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13 }}>
                    {currentStore.rating ? `${currentStore.rating.average?.toFixed(1) || '0.0'} (${currentStore.rating.count || 0} نظر)` : 'بدون امتیاز'}
                  </div>
                </div>
                {currentStore.slug && (
                  <div className="form-field">
                    <label>Slug</label>
                    <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13, direction: 'ltr', textAlign: 'right' }}>{currentStore.slug}</div>
                  </div>
                )}
                {((currentStore as any).owner || currentStore.userId) && (
                  <div className="form-field">
                    <label>مالک فروشگاه</label>
                    <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13 }}>
                      {(() => {
                        // First check if owner field exists (has complete info)
                        const owner = (currentStore as any).owner;
                        if (owner) {
                          const name = owner.name || owner.fullName ||
                            (owner.firstName && owner.lastName ? `${owner.firstName} ${owner.lastName}` : null) ||
                            owner.firstName || owner.lastName;
                          const email = owner.email;
                          const phone = owner.phone;
                          if (name && email) return `${name} (${email})`;
                          if (name && phone) return `${name} (${phone})`;
                          if (name) return name;
                          if (email) return email;
                          return owner.id || owner._id || '-';
                        }
                        // Fallback to userId if owner not available
                        if (typeof currentStore.userId === 'object') {
                          const user = currentStore.userId as any;
                          const name = user.name || user.fullName ||
                            (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : null) ||
                            user.firstName || user.lastName;
                          const email = user.email;
                          if (name && email) return `${name} (${email})`;
                          if (name) return name;
                          if (email) return email;
                          return user._id || '-';
                        }
                        return formatUserId(currentStore.userId as string);
                      })()}
                    </div>
                  </div>
                )}
              </div>

              {/* Location Info */}
              {(currentStore.address || currentStore.city || currentStore.province || currentStore.postalCode) && (
                <div>
                  <h4 style={{ borderRight: '3px solid var(--color-info)', paddingRight: 8, marginBottom: 12 }}>اطلاعات مکانی</h4>
                  <div className="form-grid">
                    {currentStore.province && (
                      <div className="form-field">
                        <label>استان</label>
                        <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13 }}>{currentStore.province}</div>
                      </div>
                    )}
                    {currentStore.city && (
                      <div className="form-field">
                        <label>شهر</label>
                        <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13 }}>{currentStore.city}</div>
                      </div>
                    )}
                    {currentStore.postalCode && (
                      <div className="form-field">
                        <label>کد پستی</label>
                        <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13 }}>{currentStore.postalCode}</div>
                      </div>
                    )}
                    {currentStore.address && (
                      <div className="form-field full">
                        <label>آدرس</label>
                        <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13 }}>{currentStore.address}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Description */}
              {currentStore.description && (
                <div>
                  <h4 style={{ borderRight: '3px solid var(--color-border)', paddingRight: 8, marginBottom: 12 }}>توضیحات</h4>
                  <div style={{ padding: 12, background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                    {currentStore.description}
                  </div>
                </div>
              )}

              {/* Categories */}
              {currentStore.categories && currentStore.categories.length > 0 && (
                <div>
                  <h4 style={{ borderRight: '3px solid var(--color-border)', paddingRight: 8, marginBottom: 12 }}>دسته‌بندی‌ها</h4>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {currentStore.categories.map((cat: any, idx: number) => (
                      <span key={idx} className="tag tag-sm tag-platform">
                        {typeof cat === 'object' ? (cat.name || cat._id) : cat}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Social Links */}
              {currentStore.socialLinks && (currentStore.socialLinks.instagram || currentStore.socialLinks.telegram || currentStore.socialLinks.whatsapp || currentStore.socialLinks.website || (currentStore.socialLinks as any).twitter || (currentStore.socialLinks as any).facebook) && (
                <div>
                  <h4 style={{ borderRight: '3px solid var(--color-border)', paddingRight: 8, marginBottom: 12 }}>شبکه‌های اجتماعی</h4>
                  <div className="form-grid">
                    {currentStore.socialLinks.instagram && (
                      <div className="form-field">
                        <label>اینستاگرام</label>
                        <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13, direction: 'ltr', textAlign: 'right' }}>
                          <a href={currentStore.socialLinks.instagram} target="_blank" rel="noreferrer" style={{ color: 'var(--color-primary)' }}>
                            {currentStore.socialLinks.instagram}
                          </a>
                        </div>
                      </div>
                    )}
                    {currentStore.socialLinks.telegram && (
                      <div className="form-field">
                        <label>تلگرام</label>
                        <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13, direction: 'ltr', textAlign: 'right' }}>
                          <a href={currentStore.socialLinks.telegram} target="_blank" rel="noreferrer" style={{ color: 'var(--color-primary)' }}>
                            {currentStore.socialLinks.telegram}
                          </a>
                        </div>
                      </div>
                    )}
                    {currentStore.socialLinks.whatsapp && (
                      <div className="form-field">
                        <label>واتساپ</label>
                        <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13, direction: 'ltr', textAlign: 'right' }}>
                          {currentStore.socialLinks.whatsapp}
                        </div>
                      </div>
                    )}
                    {(currentStore.socialLinks as any).twitter && (
                      <div className="form-field">
                        <label>توییتر</label>
                        <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13, direction: 'ltr', textAlign: 'right' }}>
                          <a href={(currentStore.socialLinks as any).twitter} target="_blank" rel="noreferrer" style={{ color: 'var(--color-primary)' }}>
                            {(currentStore.socialLinks as any).twitter}
                          </a>
                        </div>
                      </div>
                    )}
                    {(currentStore.socialLinks as any).facebook && (
                      <div className="form-field">
                        <label>فیسبوک</label>
                        <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13, direction: 'ltr', textAlign: 'right' }}>
                          <a href={(currentStore.socialLinks as any).facebook} target="_blank" rel="noreferrer" style={{ color: 'var(--color-primary)' }}>
                            {(currentStore.socialLinks as any).facebook}
                          </a>
                        </div>
                      </div>
                    )}
                    {currentStore.socialLinks.website && (
                      <div className="form-field">
                        <label>وبسایت</label>
                        <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13, direction: 'ltr', textAlign: 'right' }}>
                          <a href={currentStore.socialLinks.website} target="_blank" rel="noreferrer" style={{ color: 'var(--color-primary)' }}>
                            {currentStore.socialLinks.website}
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Verification Notes */}
              {currentStore.verificationNotes && (
                <div style={{ padding: 12, background: 'var(--color-info-bg)', borderRadius: 6, borderRight: '3px solid var(--color-info)' }}>
                  <div style={{ fontSize: 12, color: 'var(--color-info-dark)', fontWeight: 600, marginBottom: 4 }}>یادداشت تأیید</div>
                  <div style={{ fontSize: 13 }}>{currentStore.verificationNotes}</div>
                </div>
              )}

              {/* Commission Notes */}
              {currentStore.commissionNotes && (
                <div style={{ padding: 12, background: 'var(--color-warning-bg)', borderRadius: 6, borderRight: '3px solid var(--color-warning)' }}>
                  <div style={{ fontSize: 12, color: 'var(--color-warning-dark)', fontWeight: 600, marginBottom: 4 }}>یادداشت کمیسیون</div>
                  <div style={{ fontSize: 13 }}>{currentStore.commissionNotes}</div>
                </div>
              )}

              {/* Status History */}
              {currentStore.statusChangeHistory && currentStore.statusChangeHistory.length > 0 && (
                <div>
                  <h4 style={{ borderRight: '3px solid var(--color-primary)', paddingRight: 8, marginBottom: 12 }}>تاریخچه تغییرات وضعیت</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {currentStore.statusChangeHistory.map((change: any, index: number) => (
                      <div key={index} style={{ padding: 12, background: 'var(--color-bg-default)', borderRadius: 6, borderRight: '3px solid var(--color-primary)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12, color: 'var(--color-text-secondary)' }}>
                          <span>{formatDate(change.changedAt)}</span>
                          <span>{formatUserId(change.changedBy)}</span>
                        </div>
                        <div style={{ fontSize: 13 }}>
                          از <strong>{formatStatus(change.fromStatus)}</strong> به <strong>{formatStatus(change.toStatus)}</strong>
                          {change.reason && <span style={{ display: 'block', marginTop: 4, color: 'var(--color-text-secondary)' }}>دلیل: {change.reason}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Commission History */}
              {currentStore.commissionChangeHistory && currentStore.commissionChangeHistory.length > 0 && (
                <div>
                  <h4 style={{ borderRight: '3px solid var(--color-border)', paddingRight: 8, marginBottom: 12 }}>تاریخچه تغییر کمیسیون</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {currentStore.commissionChangeHistory.map((change: any, index: number) => (
                      <div key={index} style={{ padding: 12, background: 'var(--color-bg-default)', borderRadius: 6, borderRight: '3px solid var(--color-border)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12, color: 'var(--color-text-secondary)' }}>
                          <span>{formatDate(change.changedAt)}</span>
                          <span>{formatUserId(change.changedBy)}</span>
                        </div>
                        <div style={{ fontSize: 13 }}>
                          از <strong>{formatCommissionHistoryValue(change.fromCommission, change.fromTiers, change.fromFixed, change.fromFixedTiers)}</strong>{' '}
                          به <strong>{formatCommissionHistoryValue(change.toCommission, change.toTiers, change.toFixed, change.toFixedTiers)}</strong>
                          {change.reason && <span style={{ display: 'block', marginTop: 4, color: 'var(--color-text-secondary)' }}>دلیل: {change.reason}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Verification History */}
              {currentStore.verificationChangeHistory && currentStore.verificationChangeHistory.length > 0 && (
                <div>
                  <h4 style={{ borderRight: '3px solid var(--color-success)', paddingRight: 8, marginBottom: 12 }}>تاریخچه تغییر تأیید</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {currentStore.verificationChangeHistory.map((change: any, index: number) => (
                      <div key={index} style={{ padding: 12, background: 'var(--color-bg-default)', borderRadius: 6, borderRight: '3px solid var(--color-success)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12, color: 'var(--color-text-secondary)' }}>
                          <span>{formatDate(change.changedAt)}</span>
                          <span>{formatUserId(change.changedBy)}</span>
                        </div>
                        <div style={{ fontSize: 13 }}>
                          از <strong>{change.fromVerified ? 'تأیید شده' : 'تأیید نشده'}</strong> به <strong>{change.toVerified ? 'تأیید شده' : 'تأیید نشده'}</strong>
                          {change.reason && <span style={{ display: 'block', marginTop: 4, color: 'var(--color-text-secondary)' }}>دلیل: {change.reason}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn submit" onClick={() => setDetailsDialogOpen(false)}>بستن</button>
        </div>
      </Dialog>

      {/* Status Dialog */}
      <Dialog open={statusDialogOpen} onClose={handleCloseDialogs} maxWidth="sm" fullWidth className="store-status-dialog">
        <div className="edit-dialog-title">
          <span>تغییر وضعیت فروشگاه</span>
          <button className="details-close" onClick={handleCloseDialogs}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          <div className="edit-form">
            <div className="edit-card">
              <div className="edit-card-header">تغییر وضعیت</div>
              <div className="edit-card-body">
                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>وضعیت جدید <span style={{ color: 'var(--color-error-dark)' }}>*</span></label>
                    <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value as StoreStatus)}>
                      <option value={StoreStatus.ACTIVE}>فعال</option>
                      <option value={StoreStatus.INACTIVE}>غیرفعال</option>
                      <option value={StoreStatus.SUSPENDED}>معلق</option>
                    </select>
                  </div>
                  <div className="edit-field full">
                    <label>دلیل تغییر <span style={{ color: 'var(--color-error-dark)' }}>*</span></label>
                    <textarea
                      value={statusReason}
                      onChange={(e) => setStatusReason(e.target.value)}
                      placeholder="دلیل تغییر وضعیت را وارد کنید..."
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={handleCloseDialogs}>انصراف</button>
          <button className="edit-btn submit" onClick={handleUpdateStatus} disabled={!statusReason.trim()}>
            ذخیره تغییرات
          </button>
        </div>
      </Dialog>

      {/* Verify Dialog */}
      <Dialog open={verifyDialogOpen} onClose={handleCloseDialogs} maxWidth="sm" fullWidth className="store-verify-dialog">
        <div className="edit-dialog-title">
          <span>{currentStore?.isVerified ? 'لغو تأیید فروشگاه' : 'تأیید فروشگاه'}</span>
          <button className="details-close" onClick={handleCloseDialogs}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          <div className="edit-form">
            <div className="edit-card">
              <div className="edit-card-header">{currentStore?.isVerified ? 'لغو تأیید' : 'تأیید'}</div>
              <div className="edit-card-body">
                <div style={{ padding: 12, background: currentStore?.isVerified ? 'var(--color-warning-bg)' : 'var(--color-success-bg)', borderRadius: 8, marginBottom: 12, fontSize: 13, color: 'var(--color-text-primary)' }}>
                  {currentStore?.isVerified
                    ? 'با لغو تأیید، فروشگاه قادر به فروش محصولات نخواهد بود.'
                    : 'با تأیید فروشگاه، امکان فروش محصولات برای آن فراهم می‌شود.'}
                </div>
                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>توضیحات <span style={{ color: 'var(--color-error-dark)' }}>*</span></label>
                    <textarea
                      value={verificationNote}
                      onChange={(e) => setVerificationNote(e.target.value)}
                      placeholder="دلیل تأیید یا لغو تأیید را وارد کنید..."
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={handleCloseDialogs}>انصراف</button>
          <button
            className={currentStore?.isVerified ? 'btn btn-danger' : 'edit-btn submit'}
            onClick={() => handleUpdateVerification(!currentStore?.isVerified)}
            disabled={!verificationNote.trim()}
          >
            {currentStore?.isVerified ? 'لغو تأیید' : 'تأیید فروشگاه'}
          </button>
        </div>
      </Dialog>

      {/* Commission Dialog */}
      <Dialog open={commissionDialogOpen} onClose={handleCloseDialogs} maxWidth="sm" fullWidth className="store-commission-dialog">
        <div className="edit-dialog-title">
          <span>تنظیم کمیسیون</span>
          <button className="details-close" onClick={handleCloseDialogs}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          <div className="edit-form">
            <div className="edit-card">
              <div className="edit-card-header">کمیسیون فروشگاه</div>
              <div className="edit-card-body">
                <div style={{ padding: 12, background: 'var(--color-info-bg)', borderRadius: 8, marginBottom: 12, fontSize: 13, color: 'var(--color-text-primary)' }}>
                  کمیسیون فعلی: <strong>{describeCommission(currentStore)}</strong>
                </div>

                {/* انتخاب نوع کمیسیون */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  {([
                    { key: 'default', label: 'پیش‌فرض سیستم' },
                    { key: 'flat', label: 'درصد ثابت' },
                    { key: 'tiers', label: 'درصد پلکانی' },
                    { key: 'fixed', label: 'مبلغ ثابت' },
                    { key: 'fixed-tiers', label: 'مبلغ‌ثابت پلکانی' }
                  ] as const).map((m) => (
                    <button
                      key={m.key}
                      type="button"
                      className={`edit-btn ${commissionMode === m.key ? 'submit' : 'cancel'}`}
                      style={{ flex: '1 1 30%', padding: '8px 4px', fontSize: 12.5, whiteSpace: 'nowrap' }}
                      onClick={() => setCommissionMode(m.key)}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>

                {commissionMode === 'default' && (
                  <div style={{ padding: 10, background: 'var(--color-bg-secondary, #f5f5f5)', borderRadius: 8, fontSize: 12.5, marginBottom: 12 }}>
                    فروشگاه از طرح کمیسیون سراسری سیستم (هر حالتی که در تنظیمات فعال باشد) پیروی می‌کند.
                  </div>
                )}

                {commissionMode === 'flat' && (
                  <div className="edit-form-grid" style={{ marginBottom: 12 }}>
                    <div className="edit-field full">
                      <label>درصد کمیسیون ثابت</label>
                      <input
                        type="number"
                        value={customCommission}
                        onChange={(e) => setCustomCommission(e.target.value)}
                        placeholder="مثال: 5"
                        min={0}
                        max={100}
                      />
                    </div>
                  </div>
                )}

                {commissionMode === 'fixed' && (
                  <div style={{ marginBottom: 12 }}>
                    <div className="edit-form-grid">
                      <div className="edit-field full">
                        <label>مبلغ ثابت کارمزد هر برداشت (تومان)</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          dir="ltr"
                          value={customCommissionFixed === '' ? '' : formatPersianMoneyInput(customCommissionFixed)}
                          onChange={(e) => setCustomCommissionFixed(parsePersianMoneyInput(e.target.value))}
                          placeholder="مثال: 200,000"
                          style={{ textAlign: 'right' }}
                        />
                        <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', minHeight: 14 }}>
                          {customCommissionFixed !== '' && !isNaN(Number(customCommissionFixed))
                            ? amountToPersianWords(Number(customCommissionFixed))
                            : ''}
                        </span>
                      </div>
                    </div>
                    <div style={{ padding: 10, background: 'var(--color-info-bg)', borderRadius: 8, fontSize: 12, lineHeight: 1.9, marginTop: 8 }}>
                      از هر برداشت این مبلغ ثابت کسر می‌شود (مستقل از مبلغ برداشت). بخش معافِ بازرسی
                      مشمول نیست و اگر کل برداشت از محل بازرسی باشد کارمزدی کسر نمی‌شود. کارمزد هرگز
                      از بخش مشمول بیشتر نمی‌شود.
                    </div>
                    {customCommissionFixed !== '' && !isNaN(Number(customCommissionFixed)) && Number(customCommissionFixed) >= 50000 && (
                      <div style={{ padding: 10, background: 'var(--color-warning-bg, #fff8e1)', borderRadius: 8, fontSize: 12, lineHeight: 1.9, marginTop: 8 }}>
                        ⚠️ توجه: این مبلغ از حداقل برداشت (۵۰ هزار تومان) بیشتر یا مساوی است — برای
                        برداشت‌های کوچک، کارمزد بخش بزرگی از مبلغ (تا سقف کل بخش مشمول) را می‌گیرد.
                      </div>
                    )}
                  </div>
                )}

                {commissionMode === 'fixed-tiers' && (
                  <div style={{ marginBottom: 12 }}>
                    <div
                      style={{
                        fontSize: 12,
                        color: 'var(--color-text-primary)',
                        marginBottom: 10,
                        padding: 10,
                        background: 'var(--color-info-bg)',
                        borderRadius: 8,
                        lineHeight: 1.9
                      }}
                    >
                      <div style={{ fontWeight: 700 }}>📖 راهنما:</div>
                      برای هر پله «تا سقف» (تومان) و «مبلغ ثابت کارمزد» را وارد کنید؛ سقف آخرین پله را
                      خالی بگذارید (نامحدود). برخلاف پلکان درصدی، محاسبه <strong>بازه‌ای (bracket)</strong> است:
                      کل مبلغ برداشت در پله‌ی خودش می‌افتد و مبلغ ثابتِ همان پله کسر می‌شود.
                      <div style={{ marginTop: 4, color: 'var(--color-text-secondary)' }}>
                        مثال: تا ۵ میلیون → ۱۰۰ هزار، تا ۲۰ میلیون → ۲۵۰ هزار، نامحدود → ۵۰۰ هزار.
                        برداشت ۸ میلیونی = ۲۵۰ هزار تومان کارمزد. توجه: در مرز پله‌ها کارمزد
                        «پرش» دارد (۵٬۰۰۰٬۰۰۰ → ۱۰۰ هزار ولی ۵٬۰۰۰٬۰۰۱ → ۲۵۰ هزار).
                      </div>
                    </div>
                    {fixedTierRows.map((row, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
                        <span style={{ fontSize: 12, minWidth: 42, paddingTop: 8 }}>پله {toPersianDigitsStr(i + 1)}</span>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <input
                            type="text"
                            inputMode="numeric"
                            dir="ltr"
                            value={row.upTo === '' ? '' : formatPersianMoneyInput(row.upTo)}
                            onChange={(e) => {
                              const next = [...fixedTierRows];
                              next[i] = { ...next[i], upTo: parsePersianMoneyInput(e.target.value) };
                              setFixedTierRows(next);
                            }}
                            placeholder={i === fixedTierRows.length - 1 ? 'نامحدود' : 'تا سقف (تومان)'}
                            style={{ padding: '6px 8px', textAlign: 'right' }}
                          />
                          <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', minHeight: 14 }}>
                            {row.upTo === ''
                              ? 'نامحدود — هر مبلغی بالاتر از پله قبل'
                              : amountToPersianWords(Number(row.upTo))}
                          </span>
                        </div>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <input
                            type="text"
                            inputMode="numeric"
                            dir="ltr"
                            value={row.amount === '' ? '' : formatPersianMoneyInput(row.amount)}
                            onChange={(e) => {
                              const next = [...fixedTierRows];
                              next[i] = { ...next[i], amount: parsePersianMoneyInput(e.target.value) };
                              setFixedTierRows(next);
                            }}
                            placeholder="کارمزد ثابت (تومان)"
                            style={{ padding: '6px 8px', textAlign: 'right' }}
                          />
                          <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', minHeight: 14 }}>
                            {row.amount !== '' && !isNaN(Number(row.amount))
                              ? amountToPersianWords(Number(row.amount))
                              : ''}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="edit-btn cancel"
                          style={{ padding: '4px 10px', marginTop: 4 }}
                          disabled={fixedTierRows.length <= 1}
                          onClick={() => setFixedTierRows(fixedTierRows.filter((_, idx) => idx !== i))}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="edit-btn cancel"
                      style={{ padding: '6px 12px', fontSize: 12.5 }}
                      onClick={() => setFixedTierRows([...fixedTierRows, { upTo: '', amount: '' }])}
                    >
                      + افزودن پله
                    </button>

                    {/* پیش‌نمایش زنده — پلهٔ منطبق + هشدار پرش مرزی */}
                    <div style={{ marginTop: 10, padding: 10, background: 'var(--color-info-bg)', borderRadius: 8, fontSize: 12.5 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 2, flexWrap: 'wrap' }}>
                        <span>🧮 پیش‌نمایش برای برداشت:</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          dir="ltr"
                          value={formatPersianMoneyInput(tierPreviewAmount)}
                          onChange={(e) => setTierPreviewAmount(parsePersianMoneyInput(e.target.value))}
                          style={{ width: 150, padding: '4px 8px', textAlign: 'right' }}
                        />
                        <span>تومان</span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 8 }}>
                        {amountToPersianWords(Number(tierPreviewAmount) || 0)}
                      </div>
                      {(() => {
                        const amount = Number(tierPreviewAmount) || 0;
                        const tiers: CommissionFixedTier[] = fixedTierRows
                          .filter((r) => r.amount.trim() !== '' && !isNaN(Number(r.amount)))
                          .map((r) => ({ upTo: r.upTo.trim() === '' ? null : Number(r.upTo), amount: Number(r.amount) }));
                        if (amount <= 0 || tiers.length === 0) return <span>—</span>;
                        const { fee, bracket } = computeFixedTierFeePreview(amount, tiers);
                        const effective = amount > 0 ? ((fee / amount) * 100).toFixed(2) : '0';
                        return (
                          <div>
                            {bracket && (
                              <div style={{ borderTop: '1px dashed var(--color-border)', paddingTop: 6, display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                <span>
                                  پله {toPersianDigitsStr(bracket.index)} —{' '}
                                  {bracket.to === null
                                    ? `مازاد بر ${formatPersianMoneyInput(bracket.from)}`
                                    : bracket.from === 0
                                      ? `تا ${formatPersianMoneyInput(bracket.to)}`
                                      : `از ${formatPersianMoneyInput(bracket.from)} تا ${formatPersianMoneyInput(bracket.to)}`}
                                  : مبلغ ثابت {formatPersianMoneyInput(bracket.amount)}
                                </span>
                                <strong>{formatPersianMoneyInput(fee)} تومان</strong>
                              </div>
                            )}
                            <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 6, paddingTop: 6, display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 12 }}>
                              <span>کارمزد ({amountToPersianWords(fee)})</span>
                              <span>{formatPersianMoneyInput(fee)} تومان</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2, fontSize: 12 }}>
                              <span>خالص دریافتی فروشنده</span>
                              <strong>{formatPersianMoneyInput(amount - fee)} تومان</strong>
                            </div>
                            <div style={{ marginTop: 6, fontSize: 11, color: 'var(--color-text-secondary)', lineHeight: 1.8 }}>
                              نرخ مؤثر: <strong>{toPersianDigitsStr(effective)}٪</strong> از مبلغ برداشت.
                            </div>
                            {bracket && bracket.amount >= amount && (
                              <div style={{ marginTop: 6, padding: 8, background: 'var(--color-warning-bg, #fff8e1)', borderRadius: 6, fontSize: 11.5, lineHeight: 1.8 }}>
                                ⚠️ کارمزد پیکربندی‌شده ({formatPersianMoneyInput(bracket.amount)}) از خود مبلغ
                                بیشتر/مساوی است — عملاً کل بخش مشمول کسر می‌شود. پله‌ی اول را کوچک‌تر بگیرید.
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {commissionMode === 'tiers' && (
                  <div style={{ marginBottom: 12 }}>
                    <div
                      style={{
                        fontSize: 12,
                        color: 'var(--color-text-primary)',
                        marginBottom: 10,
                        padding: 10,
                        background: 'var(--color-info-bg)',
                        borderRadius: 8,
                        lineHeight: 1.9
                      }}
                    >
                      <div style={{ fontWeight: 700 }}>📖 راهنما:</div>
                      برای هر پله «تا سقف» (تومان) و «درصد» را وارد کنید؛ سقف آخرین پله را خالی بگذارید (نامحدود).
                      محاسبه <strong>حاشیه‌ای</strong> است: هر بخش از مبلغ فقط نرخ بازه‌ی خودش را می‌گیرد.
                      <div style={{ marginTop: 4, color: 'var(--color-text-secondary)' }}>
                        مثال: تا ۲۰ میلیون → ۵٪، تا ۹۹ میلیون → ۲٪، نامحدود → ۰.۵٪.
                        برداشت ۱۵۰ میلیونی = ۲۰م×۵٪ + ۷۹م×۲٪ + ۵۱م×۰.۵٪ = ۲٬۸۳۵٬۰۰۰ تومان کارمزد.
                        نتیجه را در پیش‌نمایش پایین ببینید.
                      </div>
                    </div>
                    {tierRows.map((row, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
                        <span style={{ fontSize: 12, minWidth: 42, paddingTop: 8 }}>پله {toPersianDigitsStr(i + 1)}</span>
                        <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <input
                            type="text"
                            inputMode="numeric"
                            dir="ltr"
                            value={row.upTo === '' ? '' : formatPersianMoneyInput(row.upTo)}
                            onChange={(e) => {
                              const next = [...tierRows];
                              next[i] = { ...next[i], upTo: parsePersianMoneyInput(e.target.value) };
                              setTierRows(next);
                            }}
                            placeholder={i === tierRows.length - 1 ? 'نامحدود' : 'تا سقف (تومان)'}
                            style={{ padding: '6px 8px', textAlign: 'right' }}
                          />
                          <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', minHeight: 14 }}>
                            {row.upTo === ''
                              ? 'نامحدود — هر مبلغی بالاتر از پله قبل'
                              : amountToPersianWords(Number(row.upTo))}
                          </span>
                        </div>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <input
                            type="number"
                            value={row.percent}
                            onChange={(e) => {
                              const next = [...tierRows];
                              next[i] = { ...next[i], percent: e.target.value };
                              setTierRows(next);
                            }}
                            placeholder="درصد"
                            style={{ padding: '6px 8px' }}
                            min={0}
                            max={100}
                            step={0.1}
                          />
                          <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', minHeight: 14 }}>
                            {Number(row.percent) > 0 ? `${toPersianDigitsStr(row.percent)} درصد کارمزد` : ''}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="edit-btn cancel"
                          style={{ padding: '4px 10px', marginTop: 4 }}
                          disabled={tierRows.length <= 1}
                          onClick={() => setTierRows(tierRows.filter((_, idx) => idx !== i))}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="edit-btn cancel"
                      style={{ padding: '6px 12px', fontSize: 12.5 }}
                      onClick={() => setTierRows([...tierRows, { upTo: '', percent: '' }])}
                    >
                      + افزودن پله
                    </button>

                    {/* پیش‌نمایش زنده با تفکیک پله‌ها */}
                    <div style={{ marginTop: 10, padding: 10, background: 'var(--color-info-bg)', borderRadius: 8, fontSize: 12.5 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 2, flexWrap: 'wrap' }}>
                        <span>🧮 پیش‌نمایش برای برداشت:</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          dir="ltr"
                          value={formatPersianMoneyInput(tierPreviewAmount)}
                          onChange={(e) => setTierPreviewAmount(parsePersianMoneyInput(e.target.value))}
                          style={{ width: 150, padding: '4px 8px', textAlign: 'right' }}
                        />
                        <span>تومان</span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 8 }}>
                        {amountToPersianWords(Number(tierPreviewAmount) || 0)}
                      </div>
                      {(() => {
                        const amount = Number(tierPreviewAmount) || 0;
                        const tiers: CommissionTier[] = tierRows
                          .filter((r) => r.percent.trim() !== '' && !isNaN(Number(r.percent)))
                          .map((r) => ({ upTo: r.upTo.trim() === '' ? null : Number(r.upTo), percent: Number(r.percent) }));
                        if (amount <= 0 || tiers.length === 0) return <span>—</span>;
                        const { fee, rows } = computeMarginalFeePreview(amount, tiers);
                        const effective = amount > 0 ? ((fee / amount) * 100).toFixed(2) : '0';
                        return (
                          <div>
                            <div style={{ borderTop: '1px dashed var(--color-border)', paddingTop: 6 }}>
                              {rows.map((r) => (
                                <div key={r.index} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontSize: 12 }}>
                                  <span>
                                    پله {toPersianDigitsStr(r.index)} —{' '}
                                    {r.to === null
                                      ? `مازاد بر ${formatPersianMoneyInput(r.from)}`
                                      : r.from === 0
                                        ? `تا ${formatPersianMoneyInput(r.to)}`
                                        : `از ${formatPersianMoneyInput(r.from)} تا ${formatPersianMoneyInput(r.to)}`}
                                    : {formatPersianMoneyInput(r.portion)} × {toPersianDigitsStr(r.percent)}٪
                                  </span>
                                  <strong>{formatPersianMoneyInput(r.fee)} تومان</strong>
                                </div>
                              ))}
                            </div>
                            <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 6, paddingTop: 6, display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 12 }}>
                              <span>جمع کارمزد ({amountToPersianWords(fee)})</span>
                              <span>{formatPersianMoneyInput(fee)} تومان</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2, fontSize: 12 }}>
                              <span>خالص دریافتی فروشنده</span>
                              <strong>{formatPersianMoneyInput(amount - fee)} تومان</strong>
                            </div>
                            <div style={{ marginTop: 6, fontSize: 11, color: 'var(--color-text-secondary)', lineHeight: 1.8 }}>
                              نرخ مؤثر: <strong>{toPersianDigitsStr(effective)}٪</strong> — میانگین وزنی کارمزد روی کل مبلغ
                              (جمع کارمزد ÷ کل مبلغ × {toPersianDigitsStr(100)}).
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}

                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>یادداشت <span style={{ color: 'var(--color-error-dark)' }}>*</span></label>
                    <textarea
                      value={commissionNotes}
                      onChange={(e) => setCommissionNotes(e.target.value)}
                      placeholder="توضیحات مربوط به تنظیم کمیسیون..."
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={handleCloseDialogs}>انصراف</button>
          <button className="edit-btn submit" onClick={handleUpdateCommission} disabled={!commissionNotes.trim() || loading}>
            {loading && <CircularProgress size={16} style={{ color: '#fff' }} />}
            ذخیره تغییرات
          </button>
        </div>
      </Dialog>

      {/* 🔓 آزادسازی وجه فروشنده — مدت گارانتی اختصاصی + آزادسازی/تعویق دستی */}
      {escrowStore && (
        <StoreEscrowDialog
          open
          storeId={escrowStore._id}
          storeName={escrowStore.name}
          onClose={() => setEscrowStore(null)}
          onChanged={fetchStores}
        />
      )}
    </div>
  );
};

export default StoreManagement;
